import { Badge, Rating, Typography } from '@growdash/design-system/atoms';
import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpRight } from 'lucide-react';
import BrandLink from '../../../components/atoms/brand-link';
import { Brand } from '../../../generated';

// ----------------------------------------------------------------------

const columns: ColumnDef<Brand>[] = [
  {
    accessorKey: 'brand_name',
    header: 'Brand',
    cell: ({ row }) => (
      <BrandLink id={row.original.id ?? '#'}>
        <div className="flex items-center gap-2">
          <GrowintelImage
            className="w-6 h-6"
            src={row.original.brand_image ?? ''}
          />
          <p>{row.original.brand_name}</p>
        </div>
      </BrandLink>
    ),
  },
  {
    accessorKey: 'total_branches',
    header: 'Total Branches',
  },
  {
    accessorKey: 'median_price',
    header: 'Median Price',
    cell: ({ row }) => {
      const currency = useCurrency();
      const value = row.original.median_price;
      return value && value > 0 ? `${value} ${currency}` : '-';
    },
  },
  {
    accessorKey: 'total_items',
    header: 'Total Items',
  },
  {
    accessorKey: 'avg_delivery_time',
    header: 'Avg Delivery Time',
    cell: ({ row }) => {
      const value = row.original.avg_delivery_time;
      return value && value > 0 ? `${value.toFixed(0)} min` : '-';
    },
  },
  {
    accessorKey: 'cuisines',
    header: 'Cuisines',
    cell: ({ row }) => {
      const cuisines = row.original.cuisines.split(',');
      return (
        <div className="flex gap-2">
          {cuisines.slice(0, 2).map((cuisine) => (
            <Badge key={cuisine} variant="secondary" className="capitalize">
              {cuisine}
            </Badge>
          ))}
          <div>
            {cuisines.length > 3 ? (
              <Badge variant="secondary">+{cuisines.length - 3}</Badge>
            ) : null}
          </div>
        </div>
      );
    },
  },

  {
    accessorKey: 'total_rating_count',
    header: 'Rating / Total',
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-1">
          <Rating
            size="small"
            value={Math.max(0, row.original.avg_rating_score ?? 0)}
            readOnly
          />
          <Typography>{row.original.total_rating_count}</Typography>
        </div>
      );
    },
  },
  {
    accessorKey: '',
    header: 'Detail',
    cell: ({ row }) => (
      <BrandLink id={row.original.id ?? ''}>
        <ArrowUpRight className="text-muted-foreground hover:text-primary" />
      </BrandLink>
    ),
  },
];

export default columns;
